import React, { useEffect, useState } from 'react';
import {
    Order,
    OrderItem,
    useAddItemToCartMutation,
    useCreateCheckoutMutation,
    useDutchieStore,
    useGetCustomers,
    useI18n,
    useSpringBigStore,
    useUpdateQuantityCartMutation
} from '@springbig/shared';
import {
    VStack,
    HStack,
    Image,
    Pressable,
    Box,
    Actionsheet,
    Button,
    ScrollView,
    View
} from 'native-base';
import { Caption, H1, H2 } from '~/shared/Typography';
import Section from '~/shared/Section';
import { format } from 'date-fns';
import { useShopStyles } from '~/shared/styles';
import { TabSelector } from '~/pages/shop/components/TabSelector';
import { OrderDetailPage } from '~/pages/shop/OrderDetailPage';
import EmailVerificationForm from '~/shared/EmailVerification/EmailVerificationForm';
import { useAppNavigate } from '..';
import { useToast } from '~/hooks/toast';
import {
    AddItemToCartResponse,
    CreateCheckoutResponse,
    UpdateQuantityCartResponse
} from '@springbig/shared/src/services/dutchie/mutations';

export const OrdersPage = () => {

    const { t } = useI18n();
    const { member, walletId } = useSpringBigStore();
    const {
        section_background,
        text,
        button_background,
        button_text,
        pressable_text
    } = useShopStyles();
    const [showEmailVerfication, setShowEmailVerfication] = useState(false);
    const { params, navigate } = useAppNavigate();
    const {
        orders,
        retailer,
        setOrders,
        menuType,
        checkout,
        fullfillmentOption,
        setCheckout
    } = useDutchieStore();
    const { mutate: getCustomers } = useGetCustomers();

    const [selectedOrder, setSelectedOrder] = useState<Order | null>();

    type Status = 'open' | 'closed';

    const tabs = [
        {
            name: 'current cart',
            action: () => navigate(`w/${params.wallet_id}/shop/cart`),
        },
        {
            name: 'past orders',
            isSelected: true,
        },
    ];

    useEffect(() => {

        if (orders?.length === 0 && member && member?.consumer.email_validated && member?.consumer.email) {
            getCustomers({
                'retailerId': retailer?.id,
                'filters': { 'email': member?.consumer.email }
            }, {
                onSuccess(data) {

                    const firstFive = data.customers.splice(0, 4);
                    const firstFiveOrders: Array<Order> = [];
                    firstFive.forEach((customer) => {

                        customer.orders?.forEach((order) => {

                            firstFiveOrders.push(order);
                        });
                    });
                    firstFiveOrders.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));
                    if (firstFiveOrders) {
                        setOrders(firstFiveOrders);
                    }
                },
            });
        }
    }, [member]);

    if (!member?.consumer.email_validated) {
        return <VStack h={'100%'} w={'100%'} alignItems={'center'}>
            <View py={4}>
                <TabSelector tabs={tabs}  />
            </View>
            <Caption>
                { t('shop.oops') }
            </Caption>
            <Caption>
                { t('shop.we_cant_find') }
            </Caption>
            <Caption>
                { t('shop.add_your_email') }
            </Caption>
            <Button
                background={button_background}
                my={4}
                onPress={() => setShowEmailVerfication(true)}
            >
                <Caption>
                    { t('shop.add_email') }
                </Caption>
            </Button>

            <Actionsheet isOpen={showEmailVerfication} onClose={() => setShowEmailVerfication(false)} >
                <Actionsheet.Content
                    minH={600}
                    overflow='scroll'
                >
                    <Box>
                        <H2 textAlign={'center'}>
                            { t('shop.update_your_email') }
                        </H2>
                        {walletId && <EmailVerificationForm
                            onCancel={() => setShowEmailVerfication(false)}
                            onProceed={() => setShowEmailVerfication(false)}
                            t={t}
                            walletId={walletId}
                            emailVerificationFormType='ordersPage'
                        />}
                    </Box>
                </Actionsheet.Content>
            </Actionsheet>
        </VStack>;
    }

    const { mutate: addItem } = useAddItemToCartMutation();
    const { mutate: createCheckout } = useCreateCheckoutMutation();
    const { mutate: updateQuantity } = useUpdateQuantityCartMutation();
    const { errorToast } = useToast();

    const onReOrderError = (error, orderItem: OrderItem) => {

        if (error.data.errors[0].message === 'Not Found') {
            createCheckout({
                pricingType: menuType,
                orderType: fullfillmentOption?.key,
                retailerId: retailer?.id,
            });
        }
        else if (error.data.errors[0].message === 'Validation errors') {
            updateQuantity({
                retailerId: retailer?.id,
                checkoutId: checkout?.id,
                itemId: checkout?.items.find((e) => e.product.id === orderItem.product?.id)?.id,
                quantity: orderItem.quantity,
            }, {

                onSuccess(addItemData: UpdateQuantityCartResponse) {

                    setCheckout(addItemData.updateQuantity);
                    navigate(`w/${params.wallet_id}/shop/cart`);
                },
                onError: (errorUpdate) => {

                    if (errorUpdate.data.errors[0].message === 'Not Found') {
                        createCheckout({
                            pricingType: menuType,
                            orderType: fullfillmentOption?.key,
                            retailerId: retailer?.id,
                        });
                    }
                    else {
                        errorToast(errorUpdate.data.errors[0].message);
                    }
                }
            });
        }
        else {
            errorToast(error.data.errors[0].message);
        }
    };

    const reorder = (order: Order) => {

        order.items.forEach((orderItem: OrderItem) => {

            if (checkout) {
                addItem({
                    retailerId: retailer?.id,
                    checkoutId: checkout?.id,
                    quantity: orderItem.quantity,
                    option: orderItem.option,
                    productId: orderItem.product?.id
                }, {
                    onSuccess(data: AddItemToCartResponse) {

                        setCheckout(data.addItem);
                        navigate(`w/${params.wallet_id}/shop/cart`);
                    },
                    onError: (error) => {

                        onReOrderError(error, orderItem);
                    }
                });
            }
            else {
                createCheckout({
                    pricingType: menuType,
                    orderType: fullfillmentOption?.key,
                    retailerId: retailer?.id,
                }, {
                    onSuccess(data: CreateCheckoutResponse) {

                        addItem({
                            retailerId: retailer?.id,
                            checkoutId: data.createCheckout?.id,
                            quantity: orderItem.quantity,
                            option: orderItem.option,
                            productId: orderItem.product?.id
                        }, {
                            onSuccess(addItemData: AddItemToCartResponse) {

                                setCheckout(addItemData.addItem);
                                // onClose();
                            },
                            onError: (error) => {

                                onReOrderError(error, orderItem);
                            }
                        });
                    },
                });
            }
        });
    };

    return ( orders ?
        selectedOrder ?
            <OrderDetailPage order={selectedOrder} onCancel={() => setSelectedOrder(null)} />
            :
            <Section h={'100%'} w={'100%'} overflowX={'hidden'} position={'relative'}>
                <TabSelector tabs={tabs} />
                <VStack space={4}>
                    <H1 mb={0} mt={4} alignSelf={'center'}>
                        { t('shop.your_orders') }
                    </H1>
                    <ScrollView w={'100%'} p={4}>
                        { orders.length === 0 ?
                            <Caption alignSelf={'center'}>
                                no previous orders
                            </Caption> :
                            orders.map((order, index) =>

                                <HStack
                                    space={2}
                                    justifyContent={'space-between'}
                                    bg={section_background}
                                    p={2}
                                    rounded={'xl'}
                                    key={index}
                                    mb={4}
                                >
                                    <Image
                                        key={index}
                                        src={order?.items[0]?.product?.image + `?w=100&h=100`}
                                        rounded={'xl'}
                                        w={'100px'}
                                        h={'100px'}
                                    />
                                    <VStack flex={1} space={1} alignItems={'center'}>
                                        <Caption color={pressable_text}>
                                            order status: { (order.status as Status) === 'open' ? 'open' : 'completed' }
                                        </Caption>
                                        <Caption>
                                            { format(new Date(order.createdAt), 'EEEE, LLLL d') }
                                        </Caption>
                                        <HStack space={2} alignItems={'center'}>
                                            <Caption>
                                                { order.items.length } { t('shop.item') }{ order.items.length > 1 ? t('shop.s') : '' }
                                            </Caption>
                                            <Box bg={text} w={1} h={1} rounded={'3xl'}></Box>
                                            <Caption>${ order.total.toFixed(2) }</Caption>
                                        </HStack>
                                        <HStack space={2} flexWrap={'wrap'}>
                                            <Pressable
                                                bg={button_background}
                                                rounded={'md'}
                                                py={1}
                                                px={2}
                                                w={24}
                                                onPress={() => reorder(order)}
                                            >
                                                <Caption color={button_text} textAlign={'center'}>
                                                    { t('shop.reorder') }
                                                </Caption>
                                            </Pressable>
                                            <Pressable
                                                bg={button_background}
                                                rounded={'md'}
                                                py={1}
                                                px={2}
                                                w={24}
                                                onPress={() => setSelectedOrder(order)}
                                            >
                                                <Caption color={button_text} textAlign={'center'}>
                                                    { t('shop.view_details') }
                                                </Caption>
                                            </Pressable>
                                        </HStack>
                                    </VStack>
                                </HStack>
                            )}
                    </ScrollView>
                </VStack>
            </Section>
        : <></>
    );
};
